import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import sideMenuConfig, { MenuSection } from '../AuthenticatedLayout/sideMenuConfig';
import strings from '../../common/Translation/Translate';
import {
  Box,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  Collapse,
  Typography,
  useMediaQuery,
  Tooltip,
  Popper,
  Paper,
  ClickAwayListener,
} from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import {
  isEnquiriesAllowed,
  Privilege,
  selectIntegration,
  setShowSideBar,
  translation,
  User,
} from '../../utils/redux/reducer/authentication-slice';
import { SvgIconComponent } from '@mui/icons-material';
import { isOperaIntegration } from '../../utils/constants/data';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useCustomNavigate } from '../../utils/hooks/useCustomNavigate';

interface MenuItem {
  key: string;
  name?: string;
  route?: string;
  icon?: SvgIconComponent;
  privilege?: string;
  children?: MenuItem[];
  superAdminOnly?: boolean;
  requiresEnquiriesAllowed?: boolean;
  requiresOperaIntegration?: boolean;
}

interface SidebarProps {
  isMobile?: boolean;
  open?: boolean;
  onToggle?: () => void;
  isScreenMdUp?: boolean;
  showSideBar?: boolean;
}

export const SIDEBAR_WIDTH = 248;
export const SIDEBAR_COLLAPSED_WIDTH = 72;

const level1ButtonSx = (theme: Theme, active: boolean, collapsed: boolean) => ({
  borderRadius: '10px',
  height: 46,
  minHeight: 46,
  px: collapsed ? 0 : 1.5,
  py: 0,
  justifyContent: collapsed ? 'center' : 'flex-start',
  backgroundColor: active ? theme.palette.primary.main : 'transparent',
  color: active ? theme.palette.primary.contrastText : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: active ? theme.palette.primary.main : theme.palette.action.hover,
  },
});

const level1TextSx = (theme: Theme, active: boolean) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: active ? theme.palette.primary.contrastText : theme.palette.text.primary,
});

const level2ButtonSx = (theme: Theme, active: boolean) => ({
  borderRadius: '8px',
  pl: 1.25,
  pr: 1.25,
  py: 0.625,
  minHeight: 34,
  backgroundColor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: active ? alpha(theme.palette.primary.main, 0.12) : theme.palette.action.hover,
    color: active ? theme.palette.primary.main : theme.palette.text.primary,
  },
});

const level2TextSx = (_theme: Theme, active: boolean) => ({
  fontSize: '0.8125rem',
  fontWeight: active ? 600 : 500,
  lineHeight: 1.35,
  color: 'inherit',
});

const Sidebar: FC<SidebarProps> = ({
  isMobile = false,
  open = true,
  onToggle,
  isScreenMdUp: propIsScreenMdUp,
  showSideBar: propShowSideBar,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useCustomNavigate();
  const location = useLocation();
  const mediaQueryIsScreenMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isScreenMdUp = propIsScreenMdUp ?? mediaQueryIsScreenMdUp;
  const showSideBar = propShowSideBar ?? open;

  // Desktop: open = expanded, !open = icon-only collapsed. Mobile drawer still hides when closed.
  const collapsed = !isMobile && !open;
  const currentWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const userPrivileges = useSelector(Privilege);
  const translationValue = useSelector(translation);
  const user = useSelector(User) as { role?: { value?: string } } | null;
  const tokenAllowsEnquiries = useSelector(isEnquiriesAllowed);
  const integration = useSelector(selectIntegration);
  const operaIntegration = isOperaIntegration(integration);

  const rootPath = `/${location.pathname.split('/')[1]}`;
  const isActiveRoute = (route?: string) => Boolean(route && rootPath === route);

  const itemHasActiveChild = (item: MenuItem) =>
    Boolean(item.children?.some(child => child.key !== 'divider' && isActiveRoute(child.route)));

  const menuSections: MenuSection[] = useMemo(() => {
    // Access translationValue to satisfy dependency and refresh menu when locale changes
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    translationValue;
    return sideMenuConfig(strings);
  }, [translationValue]);

  const [submenuOpenState, setSubmenuOpenState] = useState(() =>
    menuSections.reduce((acc, section) => {
      section.items.forEach(item => {
        if (item.children?.length) acc[item.key] = false;
      });
      return acc;
    }, {} as Record<string, boolean>),
  );

  const [flyoutKey, setFlyoutKey] = useState<string | null>(null);
  const flyoutAnchorRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    if (!collapsed) {
      setFlyoutKey(null);
    } else {
      setSubmenuOpenState(prev => Object.fromEntries(Object.keys(prev).map(k => [k, false])));
    }
  }, [collapsed]);

  const toggleSubmenu = (key: string) => {
    setSubmenuOpenState(prev => Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, k === key ? !v : false])));
  };

  const closeMobileIfNeeded = () => {
    if (!isScreenMdUp && onToggle) {
      onToggle();
    } else if (!isScreenMdUp) {
      dispatch(setShowSideBar(!showSideBar));
    }
  };

  const navigateTo = (route?: string) => {
    if (!route) return;
    closeMobileIfNeeded();
    navigate(route);
  };

  const filterMenuItem = (item: MenuItem): MenuItem | null => {
    if (item.superAdminOnly && user?.role?.value !== 'ROLE_BBR_ADMIN') {
      return null;
    }
    if (item.requiresOperaIntegration && !operaIntegration) {
      return null;
    }
    if (item.children) {
      const filteredChildren = item.children
        .filter(child => {
          if (child.superAdminOnly === true && user?.role?.value !== 'ROLE_BBR_ADMIN') {
            return false;
          }
          if (child.requiresOperaIntegration && !operaIntegration) {
            return false;
          }
          if (child.requiresEnquiriesAllowed && !tokenAllowsEnquiries) {
            return false;
          }
          if (child.key === 'divider') {
            return !child.privilege || userPrivileges.includes(child.privilege);
          }
          return !child.privilege || userPrivileges.includes(child.privilege);
        })
        .filter((child, index, array) => {
          if (child.key === 'divider') {
            const prevItem = array[index - 1];
            const nextItem = array[index + 1];
            return prevItem && nextItem && prevItem.key !== 'divider' && nextItem.key !== 'divider';
          }
          return true;
        });
      return filteredChildren.length ? { ...item, children: filteredChildren } : null;
    }
    return !item.privilege || userPrivileges.includes(item.privilege) ? item : null;
  };

  const filteredSections = useMemo(
    () =>
      menuSections
        .map(section => ({
          ...section,
          items: section.items.map(filterMenuItem).filter(Boolean) as MenuItem[],
        }))
        .filter(section => section.items.length > 0),
    [menuSections, userPrivileges, user, tokenAllowsEnquiries, operaIntegration],
  );

  const renderChildList = (item: MenuItem, inFlyout = false) => (
    <List component="div" disablePadding sx={{ py: inFlyout ? 0.5 : 0.25, minWidth: inFlyout ? 200 : undefined }}>
      {item.children?.map((child, index) => {
        if (child.key === 'divider') {
          return (
            <Divider
              key={`divider-${index}`}
              sx={{
                my: 0.75,
                mx: inFlyout ? 1 : undefined,
                ml: inFlyout ? undefined : 1,
                mr: inFlyout ? undefined : 0.5,
                borderColor: theme.palette.divider,
              }}
            />
          );
        }
        const childActive = isActiveRoute(child.route);
        return (
          <ListItem key={`${item.key}-${child.key}-${index}`} disablePadding sx={{ display: 'block', px: inFlyout ? 0.75 : 0 }}>
            <ListItemButton
              onClick={() => {
                setFlyoutKey(null);
                navigateTo(child.route);
              }}
              sx={level2ButtonSx(theme, childActive)}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  mr: 1.5,
                  flexShrink: 0,
                  backgroundColor: childActive ? theme.palette.primary.main : alpha(theme.palette.text.secondary, 0.75),
                }}
              />
              <ListItemText primary={child.name} primaryTypographyProps={level2TextSx(theme, childActive)} />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );

  const renderSectionLabel = (label: string, isFirst: boolean) => {
    if (!label) {
      return null;
    }

    if (collapsed) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: isFirst ? 0.75 : 1.75,
            pb: 0.75,
          }}>
          <Box
            sx={{
              width: 20,
              height: 2,
              borderRadius: 1,
              backgroundColor: theme.palette.divider,
            }}
          />
        </Box>
      );
    }

    return (
      <Typography
        component="div"
        sx={{
          px: 1.5,
          pt: isFirst ? 0.5 : 2,
          pb: 0.75,
          fontSize: '0.6875rem',
          fontWeight: 600,
          letterSpacing: '0.08em',
          lineHeight: 1.2,
          color: theme.palette.text.disabled,
          userSelect: 'none',
        }}>
        {label}
      </Typography>
    );
  };

  const renderMenuItem = (item: MenuItem) => {
    const hasChildren = Boolean(item.children?.length);
    const isSubmenuOpen = submenuOpenState[item.key] || false;
    const isFlyoutOpen = flyoutKey === item.key;
    const Icon = item.icon;
    const selfActive = isActiveRoute(item.route);
    const childActive = itemHasActiveChild(item);
    const active = selfActive || childActive;
    const chevronColor = active ? theme.palette.primary.contrastText : theme.palette.text.secondary;

    const button = (
      <ListItemButton
        ref={el => {
          flyoutAnchorRefs.current[item.key] = el;
        }}
        onClick={() => {
          if (hasChildren) {
            if (collapsed) {
              setFlyoutKey(prev => (prev === item.key ? null : item.key));
            } else {
              toggleSubmenu(item.key);
            }
          } else {
            setFlyoutKey(null);
            navigateTo(item.route);
          }
        }}
        sx={level1ButtonSx(theme, active, collapsed)}>
        {Icon && (
          <ListItemIcon
            sx={{
              minWidth: collapsed ? 0 : 36,
              color: 'inherit',
              justifyContent: 'center',
            }}>
            <Icon sx={{ fontSize: 20, color: active ? theme.palette.primary.contrastText : 'inherit' }} />
          </ListItemIcon>
        )}
        {!collapsed && (
          <>
            <ListItemText primary={item.name} primaryTypographyProps={level1TextSx(theme, active)} />
            {hasChildren && (
              <Box sx={{ ml: 0.5, display: 'flex', alignItems: 'center', opacity: 0.7 }}>
                {isSubmenuOpen ? (
                  <KeyboardArrowUpIcon sx={{ fontSize: 18, color: chevronColor }} />
                ) : (
                  <KeyboardArrowDownIcon sx={{ fontSize: 18, color: chevronColor }} />
                )}
              </Box>
            )}
          </>
        )}
        {collapsed && hasChildren && (
          <KeyboardArrowRightIcon
            sx={{
              position: 'absolute',
              right: 2,
              fontSize: 14,
              opacity: 0.55,
              color: active ? theme.palette.primary.contrastText : theme.palette.text.secondary,
            }}
          />
        )}
      </ListItemButton>
    );

    return (
      <Box key={item.key} sx={{ mb: !collapsed && hasChildren && isSubmenuOpen ? 0.25 : 0.5, position: 'relative' }}>
        <ListItem disablePadding sx={{ display: 'block' }}>
          {collapsed ? (
            <Tooltip title={isFlyoutOpen ? '' : item.name || ''} placement="right" enterDelay={400}>
              <Box sx={{ display: 'block' }}>{button}</Box>
            </Tooltip>
          ) : (
            button
          )}
        </ListItem>

        {!collapsed && hasChildren && (
          <Collapse in={isSubmenuOpen} timeout="auto" unmountOnExit>
            <Box
              sx={{
                ml: 4.5,
                pl: 1.25,
                mr: 0.5,
                mb: 0.5,
                borderLeft: `1px solid ${theme.palette.divider}`,
              }}>
              {renderChildList(item)}
            </Box>
          </Collapse>
        )}

        {collapsed && hasChildren && (
          <Popper
            open={isFlyoutOpen}
            anchorEl={flyoutAnchorRefs.current[item.key]}
            placement="right-start"
            modifiers={[
              { name: 'offset', options: { offset: [0, 8] } },
              { name: 'preventOverflow', options: { padding: 8 } },
            ]}
            style={{ zIndex: theme.zIndex.modal }}>
            <ClickAwayListener onClickAway={() => setFlyoutKey(null)}>
              <Paper
                elevation={8}
                sx={{
                  borderRadius: '10px',
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                  minWidth: 210,
                }}>
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    backgroundColor: theme.palette.action.hover,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}>
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: theme.palette.text.primary }}>
                    {item.name}
                  </Typography>
                </Box>
                {renderChildList(item, true)}
              </Paper>
            </ClickAwayListener>
          </Popper>
        )}
      </Box>
    );
  };

  const sidebarContent = (
    <Box
      sx={{
        width: currentWidth,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: theme.zIndex.drawer + 2,
        overflow: 'visible',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}>
      <Box sx={{ flex: 1, minHeight: 0, pt: 1.5, overflow: 'hidden' }}>
        <SimpleBar
          style={{
            maxHeight: '100%',
            height: '100%',
            padding: collapsed ? '8px 10px' : '8px 10px',
            borderRadius: '8px',
          }}>
          <List sx={{ padding: 0, display: 'flex', flexDirection: 'column', gap: 0 }}>
            {filteredSections.map((section, sectionIndex) => (
              <Box key={section.key}>
                {renderSectionLabel(section.label, sectionIndex === 0)}
                {section.items.map(item => (
                  <Box key={item.key}>{renderMenuItem(item)}</Box>
                ))}
              </Box>
            ))}
          </List>
        </SimpleBar>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onToggle}
        variant="temporary"
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            overflow: 'visible',
          },
        }}>
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Box
      sx={{
        width: currentWidth,
        flexShrink: 0,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}>
      {sidebarContent}
    </Box>
  );
};

export default Sidebar;
